import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import client from '../../lib/feathers';

// Hand-adjust the crop box of a grape plate.
//
// Unlike the bottle editor (AttachmentCropEditor), which locks to a square
// because the wine list shows bottles square, a plate's box is FREE: it hugs
// the vine, leaves and grapes, and the card fits it inside a 3:4 frame on a
// cream ground (decided 2026-10-05). The preview beside the stage shows exactly
// that card. Saving patches admin/grape-image with { cropBox }, which writes it
// onto the attachment as a manual box.

// Plates must be fetched with f=inside. The image service's default is fit
// "cover" into 1920 x 1080, so a bare URL (or ?w= alone) returns a LANDSCAPE
// slice of a portrait plate, which every preview then stretched back. f=inside
// keeps the true shape. (Its "original" switch, o=true, never matches: it
// compares the query string to a boolean.)
export const plateUrl = (path, w = 800) => `${process.env.REACT_APP_ATTACHMENT_URL}/${path}?w=${w}&f=inside`;

const MIN_FRAC = 0.05;
const CREAM = 'rgb(248, 243, 228)';
const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
const FULL = { x: 0, y: 0, w: 1, h: 1 };
const usable = (b) => b && b.w > 0 && b.h > 0;
const HANDLES = { nw: [0, 0, 'nwse-resize'], ne: [1, 0, 'nesw-resize'], sw: [0, 1, 'nesw-resize'], se: [1, 1, 'nwse-resize'] };

// The card: the box's region of the image, fitted (contain) inside 3:4 on cream.
export function CropPreview({ src, box, natural, width = 150 }) {
  const height = Math.round((width * 4) / 3);
  if (!natural || !usable(box)) {
    return <div style={{ width, height, background: CREAM }} />;
  }
  const regionW = box.w * natural.w;
  const regionH = box.h * natural.h;
  const scale = Math.min(width / regionW, height / regionH);
  return (
    <div style={{ width, height, background: CREAM }} className="flex items-center justify-center rounded shadow-sm">
      <div style={{ width: regionW * scale, height: regionH * scale, position: 'relative', overflow: 'hidden' }}>
        <img src={src} alt="" style={{ position: 'absolute', maxWidth: 'none', left: -box.x * natural.w * scale,
          top: -box.y * natural.h * scale, width: natural.w * scale, height: natural.h * scale }} />
      </div>
    </div>
  );
}

export default function PlateCropEditor({ image, onClose, onSaved }) {
  const attachment = image.attachment || {};
  const src = plateUrl(attachment.path, 1200);
  const [natural, setNatural] = useState(
    attachment.width && attachment.height ? { w: attachment.width, h: attachment.height } : null);
  const [box, setBox] = useState(usable(attachment.cropBox) ? attachment.cropBox : FULL);
  const [saving, setSaving] = useState(false);
  const stageRef = useRef(null);
  const drag = useRef(null);

  useEffect(() => {
    if (natural) return undefined;
    const img = new Image();
    img.onload = () => setNatural({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = src; // f=inside keeps the true aspect, which is all the box needs
    return () => { img.onload = null; };
  }, [src, natural]);

  const maxW = 420;
  const maxH = 560;
  const scale = natural ? Math.min(maxW / natural.w, maxH / natural.h) : 1;
  const W = natural ? Math.round(natural.w * scale) : maxW;
  const H = natural ? Math.round(natural.h * scale) : maxH;

  const start = (e, mode) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = stageRef.current.getBoundingClientRect();
    drag.current = { mode, startX: e.clientX, startY: e.clientY, startBox: { ...box }, rect };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end, { once: true });
  };

  const move = (e) => {
    const d = drag.current;
    if (!d) return;
    const dx = (e.clientX - d.startX) / d.rect.width;
    const dy = (e.clientY - d.startY) / d.rect.height;
    const b = d.startBox;
    if (d.mode === 'move') {
      setBox({ ...b, x: clamp(b.x + dx, 0, 1 - b.w), y: clamp(b.y + dy, 0, 1 - b.h) });
      return;
    }
    let x0 = b.x;
    let y0 = b.y;
    let x1 = b.x + b.w;
    let y1 = b.y + b.h;
    if (d.mode.includes('w')) x0 = clamp(b.x + dx, 0, x1 - MIN_FRAC);
    if (d.mode.includes('e')) x1 = clamp(b.x + b.w + dx, x0 + MIN_FRAC, 1);
    if (d.mode.includes('n')) y0 = clamp(b.y + dy, 0, y1 - MIN_FRAC);
    if (d.mode.includes('s')) y1 = clamp(b.y + b.h + dy, y0 + MIN_FRAC, 1);
    setBox({ x: x0, y: y0, w: x1 - x0, h: y1 - y0 });
  };

  const end = () => {
    drag.current = null;
    window.removeEventListener('pointermove', move);
  };

  const save = async () => {
    setSaving(true);
    try {
      const round = (n) => Math.round(n * 10000) / 10000;
      const cropBox = { x: round(box.x), y: round(box.y), w: round(box.w), h: round(box.h) };
      const updated = await client.service('admin/grape-image').patch(image.id, { cropBox, reviewed: true });
      toast.success('Crop saved');
      onSaved(updated);
    } catch (err) {
      toast.error(err.message || 'Could not save the crop');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <div className="max-h-full overflow-auto rounded-lg bg-white p-5 shadow-xl">
        <div className="mb-3 flex items-baseline justify-between gap-6">
          <h3 className="text-lg font-medium text-gray-900">{`Crop: ${image.captionName || 'plate'}`}</h3>
          <p className="text-sm text-gray-500">Drag the box to fit the vine, leaves and grapes. Corners resize.</p>
        </div>
        <div className="flex flex-wrap gap-6">
          <div ref={stageRef} className="relative select-none" style={{ width: W, height: H, touchAction: 'none' }}>
            <img src={src} alt="" className="absolute inset-0 h-full w-full" draggable={false} />
            <div className="absolute inset-0 bg-black/40"
              style={{ clipPath: `polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 0, ${box.x * 100}% ${box.y * 100}%, ${box.x * 100}% ${(box.y + box.h) * 100}%, ${(box.x + box.w) * 100}% ${(box.y + box.h) * 100}%, ${(box.x + box.w) * 100}% ${box.y * 100}%, ${box.x * 100}% ${box.y * 100}%)` }} />
            <div
              className="absolute cursor-move border-2 border-white"
              style={{ left: box.x * W, top: box.y * H, width: box.w * W, height: box.h * H, boxShadow: '0 0 0 1px rgba(0,0,0,.5)' }}
              onPointerDown={(e) => start(e, 'move')}
            >
              {Object.entries(HANDLES).map(([k, [lx, ly, cursor]]) => (
                <span key={k} onPointerDown={(e) => start(e, k)}
                  className="absolute h-3 w-3 rounded-sm border border-gray-700 bg-white"
                  style={{ left: `calc(${lx * 100}% - 6px)`, top: `calc(${ly * 100}% - 6px)`, cursor }} />
              ))}
            </div>
          </div>
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500">The card</p>
            <CropPreview src={src} box={box} natural={natural} />
            <div className="mt-6 flex flex-col gap-2">
              <button type="button" onClick={save} disabled={saving || !natural}
                className="rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm disabled:opacity-50">
                {saving ? 'Saving...' : 'Save crop'}
              </button>
              <button type="button" onClick={() => setBox(FULL)} className="rounded-md px-3 py-2 text-sm text-gray-600 ring-1 ring-gray-300">
                Whole plate
              </button>
              <button type="button" onClick={onClose} className="rounded-md px-3 py-2 text-sm text-gray-600">Cancel</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// The full plate, large, with its credit: what "one tap away" shows in the app.
export function PlateLightbox({ image, onClose }) {
  const att = image.attachment || {};
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" role="dialog" aria-modal="true"
      onClick={onClose} onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }} tabIndex={-1}>
      <div className="flex max-h-full max-w-5xl flex-col items-center gap-3" onClick={(e) => e.stopPropagation()} role="presentation">
        <img src={plateUrl(att.path, 1600)} alt={image.captionName || ''} className="max-h-[80vh] w-auto rounded shadow-2xl" />
        <div className="max-w-3xl text-center text-sm text-gray-100">
          <p>{image.credit}</p>
          {image.sourceUrl ? <a href={image.sourceUrl} target="_blank" rel="noreferrer" className="text-gray-300 underline">Source</a> : null}
        </div>
        <button type="button" onClick={onClose} className="rounded-md bg-white/10 px-4 py-1.5 text-sm text-white hover:bg-white/20">Close</button>
      </div>
    </div>
  );
}
